import React, { useEffect, useRef, useState } from "react";
import { addBaseMap } from "../lib/mapBase";
import { addMapLock } from "../lib/mapLock";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Play, Save, AlertTriangle, MapPin, Navigation, MessageSquarePlus, GripVertical, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const tl = (n, dec = 0) => (Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

export default function HesaplamaModal({ row, open, onOpenChange, onSaved }) {
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const mapRef = useRef(null);
  const [openP, setOpenPRaw] = useState(() => { try { return JSON.parse(localStorage.getItem("mwcrm_hesap_open") || '{"map":true}'); } catch { return { map: true }; } });
  const setOpenP = (f) => setOpenPRaw((o) => { const n = typeof f === "function" ? f(o) : f; localStorage.setItem("mwcrm_hesap_open", JSON.stringify(n)); return n; });
  const mapObjRef = useRef(null);
  const cardRefs = useRef({});
  const dragId = useRef(null);
  const DEFAULT_ORDER = ["varis", "cikis", "rut", "barem", "map", "notes"];
  const [layout, setLayout] = useState(() => { try { return JSON.parse(localStorage.getItem("mwcrm_hesap_layout") || "{}"); } catch { return {}; } });
  const { columns } = useAuth();
  const [cdata, setCdata] = useState({});
  useEffect(() => { setCdata(row?.data || {}); }, [row?.id, open]);
  const colOf = (k) => columns.find((c) => c.key === k);
  const STAGE_KEYS = ["ilk_temas", "asama1", "asama2", "asama3", "sonuc"];
  const saveField = async (k, v) => {
    const nd = { ...cdata, [k]: v };
    setCdata(nd);
    try { await api.put(`/customers/${row.id}`, { data: nd, archived: row.archived }); onSaved && onSaved(); }
    catch (e) { toast.error("KAYDEDİLEMEDİ"); }
  };

  useEffect(() => { setText(""); setRes(row?.data?.hesap || null); }, [row?.id, open]);

  const loadNotes = async () => {
    if (!row) return;
    try { const { data } = await api.get(`/customers/${row.id}/notes`, { silent: true }); setNotes(data); } catch (e) {}
  };
  useEffect(() => { if (open && row) { setNoteText(""); loadNotes(); } /* eslint-disable-next-line */ }, [open, row?.id]);
  const addNote = async () => {
    if (!noteText.trim() || !row) return;
    await api.post(`/customers/${row.id}/notes`, { text: noteText });
    setNoteText(""); loadNotes(); toast.success("NOT EKLENDİ");
  };

  useEffect(() => {
    const L = window.L;
    if (!open || !res || !L || !openP.map) return;
    let map = null;
    let cancelled = false;
    let tries = 0;
    const timers = [];
    const build = () => {
      if (cancelled) return;
      if (!mapRef.current) { if (tries++ < 40) timers.push(setTimeout(build, 60)); return; }
      map = L.map(mapRef.current, { center: [39, 35], zoom: 5, scrollWheelZoom: true, preferCanvas: true });
    addMapLock(map, "hesaplama");
      mapObjRef.current = map;
      addBaseMap(map, "hesap");
      const mk = (list, color, label, teklif) => {
        (list || []).forEach((x) => {
          if (x.lat == null || x.lng == null) return;
          const t = (teklif || []).find((tt) => tt.il === x.il);
          const det = t ? t.buckets.map((b) => `${b.kind} ${Math.round(b.avg_desi)} DESİ: ${tl(b.avg_fiyat, 2)} TL`).join("<br/>") : "";
          L.circleMarker([x.lat, x.lng], { radius: 8, color: "#fff", weight: 2, fillColor: color, fillOpacity: 0.9 }).addTo(map)
            .bindPopup(`<div style="min-width:150px"><b>${x.il}</b> <span style="color:#94A3B8">(${label})</span><br/>CİRO: <b>${tl(x.ciro)}</b>${det ? '<hr style="border-color:#334155;margin:4px 0"/>' + det : ""}</div>`);
        });
      };
      const v = [...(res.il_ciro?.group1 || []), ...(res.il_ciro?.group2 || [])];
      const c = [...(res.cikis_ciro?.group1 || []), ...(res.cikis_ciro?.group2 || [])];
      mk(v, "#3B82F6", "VARIŞ", res.teklif);
      mk(c, "#D946EF", "ÇIKIŞ", res.cikis_teklif);
      const pts = [...v, ...c].filter((x) => x.lat != null).map((x) => [x.lat, x.lng]);
      const fit = () => { if (cancelled || !map) return; try { map.invalidateSize(); if (pts.length) map.fitBounds(pts, { padding: [30, 30], maxZoom: 7 }); } catch (e) {} };
      fit();
      [120, 350, 700, 1100].forEach((ms) => timers.push(setTimeout(fit, ms)));
    };
    build();
    return () => { cancelled = true; timers.forEach(clearTimeout); if (map) map.remove(); mapObjRef.current = null; };
  }, [open, res, openP.map]);

  const run = async () => {
    if (!text.trim()) { toast.error("ÖNCE VERİ YAPIŞTIRIN"); return; }
    setLoading(true);
    try {
      const r = await api.post("/hesaplama/analyze", { data: { text, has_name: false } });
      setRes(r.data.aggregate);
      if (!r.data.row_count) toast.error("SATIR OKUNAMADI — FORMAT: İL · DESİ · FİYAT · YEKÜN");
      else toast.success(`${r.data.row_count} SATIR OKUNDU`);
    } finally { setLoading(false); }
  };
  const save = async () => {
    if (!res) return;
    setSaving(true);
    try {
      await api.post("/hesaplama/save", { data: { customer_id: row.id, il_ciro_text: res.il_ciro_text, rut_text: res.rut_text, teklif_text: res.teklif_text, cikis_ciro_text: res.cikis_ciro_text, hesap: { il_ciro: res.il_ciro, rut: res.rut, teklif: res.teklif, il_ciro_text: res.il_ciro_text, rut_text: res.rut_text, teklif_text: res.teklif_text, cikis_ciro: res.cikis_ciro, cikis_teklif: res.cikis_teklif, cikis_ciro_text: res.cikis_ciro_text, has_cikis: res.has_cikis } } });
      toast.success("MÜŞTERİYE KAYDEDİLDİ");
      onSaved && onSaved();
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  const order = layout.order || DEFAULT_ORDER;
  const sizes = layout.sizes || {};
  const saveLayout = (next) => { setLayout(next); try { localStorage.setItem("mwcrm_hesap_layout", JSON.stringify(next)); } catch (e) {} };
  const reorder = (from, to) => {
    if (!from || from === to) return;
    const base = order.filter((id) => id !== from);
    const idx = base.indexOf(to);
    base.splice(idx < 0 ? base.length : idx, 0, from);
    saveLayout({ ...layout, order: base, sizes });
    if (mapObjRef.current) setTimeout(() => { try { mapObjRef.current.invalidateSize(); } catch (e) {} }, 60);
  };
  const persistSize = (id) => {
    const el = cardRefs.current[id];
    if (!el) return;
    const ns = { ...sizes, [id]: { w: el.style.width || undefined, h: el.style.height || undefined } };
    saveLayout({ ...layout, order, sizes: ns });
    if (id === "map" && mapObjRef.current) setTimeout(() => { try { mapObjRef.current.invalidateSize(); } catch (e) {} }, 50);
  };
  const resetLayout = () => saveLayout({});
  const idsRef = useRef([]);
  const movePanel = (id, dir) => {
    const ids = idsRef.current;
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    const n = [...ids];
    [n[i], n[j]] = [n[j], n[i]];
    saveLayout({ ...layout, order: n, sizes });
    if (mapObjRef.current) setTimeout(() => { try { mapObjRef.current.invalidateSize(); } catch (e) {} }, 60);
  };
  const renderCard = (p) => {
    if (!p) return null;
    const size = sizes[p.id] || {};
    const wide = !!p.wide;
    const isOpen = !!openP[p.id];
    const style = !isOpen ? { width: wide ? "100%" : size.w, flexBasis: wide ? "100%" : undefined } : wide ? { width: "100%", flexBasis: "100%", height: size.h || "360px" } : { width: size.w, height: size.h };
    return (
      <div key={p.id} data-panel={p.id} ref={(el) => { cardRefs.current[p.id] = el; }}
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); reorder(dragId.current, p.id); dragId.current = null; }}
        onMouseUp={() => persistSize(p.id)} style={style}
        className={`flex min-w-[240px] max-w-full flex-1 flex-col rounded-lg border border-slate-800 bg-slate-950/40 p-2 ${isOpen ? "resize overflow-auto" : ""}`}>
        <div className={`${isOpen ? "mb-2" : ""} flex items-center gap-1 text-xs font-bold`} style={{ color: p.color }}>
          <button data-testid={`panel-toggle-${p.id}`} onClick={() => setOpenP((o) => ({ ...o, [p.id]: !o[p.id] }))} title={isOpen ? "KAPAT" : "AÇ"} className="rounded px-1 hover:bg-slate-800">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
          <button data-testid={`panel-left-${p.id}`} onClick={() => movePanel(p.id, -1)} title="SOLA / ÖNE AL" className="rounded px-1 text-slate-400 hover:bg-slate-800 hover:text-white">◀</button>
          <span draggable onDragStart={(e) => { dragId.current = p.id; e.dataTransfer.effectAllowed = "move"; }} title="SÜRÜKLE: YERİNİ DEĞİŞTİR" className="flex flex-1 cursor-move items-center gap-1 truncate"><GripVertical className="h-3.5 w-3.5 opacity-60" /> {p.title}</span>
          <button data-testid={`panel-right-${p.id}`} onClick={() => movePanel(p.id, 1)} title="SAĞA / ARKAYA AL" className="rounded px-1 text-slate-400 hover:bg-slate-800 hover:text-white">▶</button>
          {p.id === "map" && <button data-testid="map-toggle-text" onClick={() => setOpenP((o) => ({ ...o, map: !o.map }))} className="ml-1 shrink-0 rounded border border-sky-700 px-2 py-0.5 text-[10px] font-bold text-sky-300 hover:bg-sky-900/40">{isOpen ? "HARİTAYI KAPAT" : "HARİTAYI AÇ"}</button>}
        </div>
        {isOpen && <div className="min-h-0 flex-1 overflow-auto">{p.body}</div>}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl resize-y overflow-y-auto border-slate-700 bg-slate-900 text-slate-100" style={{ height: "88vh", minHeight: "420px", maxHeight: "95vh" }}>
        <DialogHeader><DialogTitle className="font-head">HESAPLAMA — {row?.data?.firma}</DialogTitle></DialogHeader>
        <div data-testid="arama-karti-ustbilgi" className="space-y-2 rounded-lg border border-slate-800 bg-slate-950/40 p-3">
          <div className="flex flex-wrap items-end gap-2">
            {STAGE_KEYS.map((k) => { const col = colOf(k); if (!col) return null; const val = cdata[k] || ""; const clr = (col.colors || {})[val];
              return (
                <div key={k} className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">{col.label}</span>
                  <select data-testid={`stage-${k}`} value={val} onChange={(e) => saveField(k, e.target.value)} style={clr ? { color: clr, fontWeight: 600 } : {}} className="h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-xs text-slate-100">
                    <option value="">—</option>
                    {(col.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              ); })}
            <div className="ml-auto flex flex-col gap-0.5 text-right text-xs">
              <span><span className="text-slate-500">HEDEF:</span> <b className="text-slate-200">{tl(cdata.hedef_ciro)}</b></span>
              <span><span className="text-slate-500">GERÇEK:</span> <b className="text-emerald-300">{tl(cdata.gerceklesen_ciro)}</b></span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-slate-800 pt-2 text-xs md:grid-cols-3">
            <div><span className="text-slate-500">YETKİLİ:</span> <span className="text-slate-200">{cdata.yetkili || "—"}</span></div>
            <div><span className="text-slate-500">GSM:</span> <a href={`tel:${cdata.gsm || ""}`} className="text-emerald-300 hover:underline">{cdata.gsm || "—"}</a></div>
            <div><span className="text-slate-500">S.TEL:</span> <a href={`tel:${cdata.s_tel || ""}`} className="text-emerald-300 hover:underline">{cdata.s_tel || "—"}</a></div>
            <div className="truncate"><span className="text-slate-500">EMAIL:</span> <a href={`mailto:${cdata.email || ""}`} className="text-sky-300 hover:underline">{cdata.email || "—"}</a></div>
            <div className="col-span-2 truncate"><span className="text-slate-500">ADRES:</span> <span className="text-slate-200">{[cdata.adres, cdata.ilce, cdata.il].filter(Boolean).join(" / ") || "—"}</span></div>
          </div>
        </div>
        <p className="text-xs text-slate-400">Bu müşteriye ait ham listeyi yapıştırın. Sütun sırası: <b>İL(varış) · DESİ · FİYAT · YEKÜN</b> · <b className="text-fuchsia-300">ÇIKIŞ İLİ (6. sütun, opsiyonel)</b> — tab veya 2+ boşlukla ayrılmış.</p>
        <textarea data-testid="hesapcell-text" value={text} onChange={(e) => setText(e.target.value)} rows={5}
          placeholder={"İZMİR\t45\t120,00\t5.400\tİSTANBUL\nBURSA\t300\t1.500,00\t1.500\tİSTANBUL"}
          className="min-h-[120px] w-full rounded-md border border-slate-700 bg-slate-800 p-3 font-mono text-sm text-slate-100" />
        <div className="flex gap-2">
          <Button data-testid="hesapcell-run" onClick={run} disabled={loading} className="bg-orange-600 text-white hover:bg-orange-700"><Play className="mr-1 h-4 w-4" />{loading ? "HESAPLANIYOR..." : "HESAPLA"}</Button>
          {res && <Button data-testid="hesapcell-save" onClick={save} disabled={saving} className="bg-emerald-600 text-white hover:bg-emerald-700"><Save className="mr-1 h-4 w-4" /> MÜŞTERİYE KAYDET</Button>}
        </div>

        {(() => {
          const defs = [];
          if (res && res.il_ciro) defs.push({ id: "varis", title: "İL CİROLARI (VARIŞ)", color: "#0369a1", body: (
            <table className="w-full text-xs"><tbody>
              {res.il_ciro.group1.map((x) => <tr key={x.il} className="text-slate-200"><td className="py-0.5">{x.il}</td><td className="py-0.5 text-right font-mono">{tl(x.ciro)}</td></tr>)}
              <tr className="border-t border-slate-700 font-bold text-sky-200"><td className="py-0.5">Ara Toplam</td><td className="py-0.5 text-right font-mono">{tl(res.il_ciro.ara1)}</td></tr>
              <tr><td colSpan={2} className="py-1"><div className="border-t border-dashed border-slate-700" /></td></tr>
              {res.il_ciro.group2.map((x) => <tr key={x.il} className="text-slate-300"><td className="py-0.5">{x.il}</td><td className="py-0.5 text-right font-mono">{tl(x.ciro)}</td></tr>)}
              <tr className="border-t border-slate-700 font-bold text-sky-200"><td className="py-0.5">Ara Toplam</td><td className="py-0.5 text-right font-mono">{tl(res.il_ciro.ara2)}</td></tr>
              <tr className="border-t-2 border-slate-600 font-bold text-orange-300"><td className="py-1">GENEL TOPLAM</td><td className="py-1 text-right font-mono">{tl(res.il_ciro.genel)}</td></tr>
            </tbody></table>
          ) });
          if (res && res.has_cikis && res.cikis_ciro) defs.push({ id: "cikis", title: "ÇIKIŞ İL CİROLARI", color: "#a21caf", body: (
            <div data-testid="cikis-ciro-card">
              <table className="w-full text-xs"><tbody>
                {res.cikis_ciro.group1.map((x) => <tr key={x.il} className="text-slate-200"><td className="py-0.5">{x.il}</td><td className="py-0.5 text-right font-mono">{tl(x.ciro)}</td></tr>)}
                {res.cikis_ciro.group1.length > 0 && <tr className="border-t border-slate-700 font-bold text-fuchsia-200"><td className="py-0.5">Ara Toplam</td><td className="py-0.5 text-right font-mono">{tl(res.cikis_ciro.ara1)}</td></tr>}
                <tr><td colSpan={2} className="py-1"><div className="border-t border-dashed border-slate-700" /></td></tr>
                {res.cikis_ciro.group2.map((x) => <tr key={x.il} className="text-slate-300"><td className="py-0.5">{x.il}</td><td className="py-0.5 text-right font-mono">{tl(x.ciro)}</td></tr>)}
                {res.cikis_ciro.group2.length > 0 && <tr className="border-t border-slate-700 font-bold text-fuchsia-200"><td className="py-0.5">Ara Toplam</td><td className="py-0.5 text-right font-mono">{tl(res.cikis_ciro.ara2)}</td></tr>}
                {res.cikis_ciro.missing_count > 0 && <tr data-testid="cikis-missing-row" className="font-bold text-rose-400"><td className="py-0.5">⚠ İL YAZILMAMIŞ/TANINMADI ({res.cikis_ciro.missing_count})</td><td className="py-0.5 text-right font-mono">{tl(res.cikis_ciro.missing)}</td></tr>}
                <tr className="border-t-2 border-slate-600 font-bold text-orange-300"><td className="py-1">GENEL TOPLAM</td><td className="py-1 text-right font-mono">{tl(res.cikis_ciro.grand)}</td></tr>
              </tbody></table>
              {res.cikis_ciro.missing_count > 0 && <div data-testid="cikis-mismatch-note" className="mt-1 text-[10px] font-semibold text-rose-400">KIRMIZI SATIRLARDAKİ {res.cikis_ciro.missing_count} KAYITTA ÇIKIŞ İLİ YOK/TANINMADI. DOĞRU İL YAZINCA VARIŞ İLE AYNI TUTAR OLUR.</div>}
            </div>
          ) });
          if (res) defs.push({ id: "rut", title: "RUT", color: "#047857", body: (
            <div className="flex flex-col gap-1">
              {res.rut.map((r) => <div key={r.il} className="flex items-center justify-between rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-200"><span className="font-mono font-bold text-emerald-300">{r.code}</span><span className="text-slate-500">{r.il}</span></div>)}
            </div>
          ) });
          if (res) defs.push({ id: "barem", title: "BAREM FİYAT TEKLİFİ", color: "#b45309", body: (
            <div className="space-y-2">
              {res.teklif.map((t) => (
                <div key={t.il}>
                  <div className="text-xs font-bold text-slate-200">{t.il}:</div>
                  {t.buckets.map((b, i) => (
                    <div key={i} className={`flex items-center justify-between rounded px-2 py-0.5 text-xs ${b.outlier ? "bg-amber-400/20 text-amber-200" : "text-slate-300"}`}>
                      <span>{b.outlier && <AlertTriangle className="mr-1 inline h-3 w-3 text-amber-400" />}{b.kind} {Math.round(b.avg_desi)} DESİ</span>
                      <span className="font-mono">{tl(b.avg_fiyat, 2)} TL</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) });
          if (res) defs.push({ id: "map", wide: true, title: "İL YOĞUNLUK HARİTASI (VARIŞ=MAVİ · ÇIKIŞ=PEMBE · TIKLA: KOLİ/PALET)", color: "#0369a1", body: (
            <div ref={mapRef} data-testid="hesap-map" className="h-full min-h-[300px] w-full overflow-hidden rounded-lg border border-slate-800" />
          ) });
          defs.push({ id: "notes", title: `NOTLAR (${notes.length})`, color: "#b45309", body: (
            <div>
              <div className="flex gap-2">
                <textarea data-testid="hesap-note-text" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="GÖRÜŞME NOTU YAZIN..." className="flex-1 rounded-md border border-slate-700 bg-slate-800 p-2 text-xs text-slate-100" />
                <Button data-testid="hesap-note-add" onClick={addNote} className="bg-blue-600 text-white hover:bg-blue-700"><MessageSquarePlus className="mr-1 h-4 w-4" /> EKLE</Button>
              </div>
              <div className="mt-2 space-y-1.5">
                {notes.length === 0 && <div className="py-3 text-center text-xs text-slate-500">HENÜZ NOT YOK</div>}
                {notes.map((n) => (
                  <div key={n.id} data-testid="hesap-note-item" className="rounded border border-slate-800 bg-slate-800/40 p-2">
                    <div className="whitespace-pre-wrap break-words text-xs text-slate-100">{n.text}</div>
                    <div className="mt-1 text-[10px] text-slate-500">{n.author || "—"} · {new Date(n.created_at).toLocaleString("tr-TR")}</div>
                  </div>
                ))}
              </div>
            </div>
          ) });
          const defMap = Object.fromEntries(defs.map((d) => [d.id, d]));
          const ids = [...order.filter((id) => defMap[id]), ...defs.filter((d) => !order.includes(d.id)).map((d) => d.id)];
          idsRef.current = ids;
          if (!ids.length) return null;
          return (
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-500">PANELLERİ BAŞLIKTAN SÜRÜKLEYİP YERİNİ DEĞİŞTİRİN • SAĞ-ALT KÖŞEDEN BOYUTLANDIRIN (OTOMATİK KAYDEDİLİR)</span>
                <button data-testid="layout-reset" onClick={resetLayout} className="shrink-0 text-[11px] text-slate-400 hover:text-slate-200">↺ DÜZENİ SIFIRLA</button>
              </div>
              <div className="flex flex-wrap items-start gap-4">{ids.map((id) => renderCard(defMap[id]))}</div>
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
